import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Rating,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Paper,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  Feedback as FeedbackIcon,
  Send,
  BugReport,
  Lightbulb,
  Chat,
  GitHub as GitHubIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { submitFeedback, fetchFeedback, clearError } from '../store/slices/usersSlice';
import { safeFormatDate } from '../utils/dateUtils';
import { useSidebar } from '../contexts/SidebarContext';
import backendUrl from '../store/config';
import FeedbackAttachmentThumb from '../components/common/FeedbackAttachmentThumb';

const Feedback: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // In the component:
  const { sidebarOpen } = useSidebar();
    
  const dispatch = useAppDispatch();
  const feedback = useAppSelector((state: any) => state.users.feedback);
  const loading = useAppSelector((state: any) => state.users.loading);
  const error = useAppSelector((state: any) => state.users.error);
  const token = useAppSelector((state: any) => state.auth?.token);

  const [formData, setFormData] = useState({
    rating: 0,
    comment: '',
    category: 'general' as 'bug' | 'feature' | 'general',
  });
  const [images, setImages] = useState<File[]>([]);

  const [submitted, setSubmitted] = useState(false);

  const safeText = (value: any): string => {
    if (value === null || value === undefined) return '';
    return typeof value === 'string' ? value : JSON.stringify(value);
  };

  const normalizeFeedbackItem = (raw: any) => {
    return {
      id: String(raw?.id ?? Math.random().toString(36).slice(2)),
      rating: typeof raw?.rating === 'number' ? raw.rating : Number(raw?.rating ?? 0),
      comment: safeText(raw?.comment),
      category: typeof raw?.category === 'string' ? raw.category : safeText(raw?.category),
      status: typeof raw?.status === 'string' ? raw.status : safeText(raw?.status),
      created_at: typeof raw?.created_at === 'string' ? raw.created_at : '',
      attachments: Array.isArray(raw?.attachments) ? raw.attachments : [],
      github_issue_number: raw?.github_issue_number ?? null,
      github_issue_url: raw?.github_issue_url ?? null,
    };
  };

  useEffect(() => {
    dispatch(fetchFeedback());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const MAX_IMAGES = 3;
  const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
  const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp']);

  const addImages = (files: FileList | File[]) => {
    const next = Array.from(files || []);
    const accepted = next.filter((f) => ALLOWED_IMAGE_TYPES.has((f.type || '').toLowerCase()));
    const sized = accepted.filter((f) => f.size <= MAX_IMAGE_SIZE_BYTES);

    setImages((prev) => {
      const merged = [...prev, ...sized].slice(0, MAX_IMAGES);
      return merged;
    });
  };

  const removeImageAt = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    if (formData.rating === 0 || (!formData.comment.trim() && images.length === 0)) {
      return;
    }

    dispatch(submitFeedback({ ...formData, images }));
    setSubmitted(true);
    setFormData({
      rating: 0,
      comment: '',
      category: 'general',
    });
    setImages([]);

    // Reset submitted state after 3 seconds
    setTimeout(() => setSubmitted(false), 3000);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'bug':
        return <BugReport sx={{ color: getCategoryColor('bug') }} />;
      case 'feature':
        return <Lightbulb sx={{ color: getCategoryColor('feature') }} />;
      default:
        return <Chat sx={{ color: getCategoryColor('general') }} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'bug':
        return '#f44336'; // Red
      case 'feature':
        return '#2196f3'; // Blue
      case 'general':
      default:
        return '#4caf50'; // Green
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'implemented':
      case 'resolved':
        return '#4caf50'; // Green
      case 'reviewed':
        return '#ff9800'; // Orange
      case 'pending':
      default:
        return '#9e9e9e'; // Gray
    }
  };

  return (
    <Box
      sx={{
        marginLeft: sidebarOpen ? '0px' : '0px',
        transition: (theme) =>
          theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        width: sidebarOpen ? 'calc(100% - 0px)' : 'calc(100% - 0px)',
      }}
    >
      <Box sx={{ 
        height: isMobile ? 'auto' : 'calc(100vh - 64px)', 
        minHeight: isMobile ? 'calc(100vh - 64px)' : 'auto',
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: 'background.default',
        overflow: isMobile ? 'visible' : 'hidden',
        p: isMobile ? 2 : 4
      }}>
      {/* Header */}
      <Box sx={{ p: isMobile ? 2 : 3, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: isMobile ? '1rem' : '1.1rem' }}>
          Feedback
        </Typography>
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 2,
              backgroundColor: 'background.paper', 
              color: 'text.secondary',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              '& .MuiAlert-icon': { color: 'text.secondary' }
            }}
          >
            {safeText(error)}
          </Alert>
        )}

        {submitted && (
          <Alert 
            severity="success" 
            sx={{ 
              mt: 2,
              backgroundColor: 'background.paper', 
              color: 'text.secondary',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              '& .MuiAlert-icon': { color: 'text.secondary' }
            }}
          >
            Thank you for your feedback! We appreciate your input.
          </Alert>
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: isMobile ? 'visible' : 'hidden' }}>

        {/* Left Panel - Submit Feedback */}
        <Box sx={{ 
          width: isMobile ? '100%' : '40%', 
          p: isMobile ? 2 : 3, 
          borderRight: isMobile ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
          borderBottom: isMobile ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
          display: 'flex',
          flexDirection: 'column',
          textAlign: isMobile ? 'center' : 'left'
        }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 400, mb: 2, color: 'text.primary', fontSize: '0.95rem' }}>
            Submit Feedback
          </Typography>

          <Box sx={{ mb: 2.5 }}>
            <Typography component="legend" sx={{ mb: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
              How would you rate your experience?
            </Typography>
            <Rating
              name="rating"
              value={formData.rating}
              onChange={(_, newValue) => {
                setFormData(prev => ({ ...prev, rating: newValue || 0 }));
              }}
              size="small"
              sx={{ 
                '& .MuiRating-iconFilled': { color: '#ffc107' },
                '& .MuiRating-iconEmpty': { color: 'action.disabled' }
              }}
            />
          </Box>

          <FormControl fullWidth sx={{ mb: 2.5 }} size="small">
            <InputLabel sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>Category</InputLabel>
            <Select
              value={formData.category}
              label="Category"
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { 
                  borderColor: 'divider',
                  transition: 'border-color 0.3s ease-in-out'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' },
                fontSize: '0.8rem'
              }}
            >
              <MenuItem value="general">General Feedback</MenuItem>
              <MenuItem value="bug">Bug Report</MenuItem>
              <MenuItem value="feature">Feature Request</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Your feedback"
            placeholder="Tell us what you think about AGRANA..."
            value={formData.comment}
            onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
            size="small"
            sx={{ 
              mb: 2.5,
              flex: 1,
              '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.8rem' },
              '& .MuiOutlinedInput-root': {
                height: '100%',
                fontSize: '0.8rem',
                alignItems: 'flex-start',
                '& textarea': {
                  paddingTop: '10px !important'
                },
                '& fieldset': { 
                  borderColor: 'divider',
                  transition: 'border-color 0.3s ease-in-out'
                },
                '&:hover fieldset': { borderColor: 'text.secondary' },
                '&.Mui-focused fieldset': { borderColor: 'text.secondary' }
              }
            }}
          />

          {/* Screenshot attachments */}
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.dataTransfer?.files) addImages(e.dataTransfer.files);
            }}
            sx={{
              mb: 2.5,
              p: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 1 }}>
              Attach screenshots (optional) — drag & drop or upload (max {MAX_IMAGES})
            </Typography>

            <Button
              component="label"
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none', mb: images.length ? 1.5 : 0 }}
            >
              Upload image
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                hidden
                multiple
                onChange={(e) => {
                  if (e.target.files) addImages(e.target.files);
                  e.target.value = '';
                }}
              />
            </Button>

            {images.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {images.map((f, idx) => (
                  <Box key={`${f.name}-${idx}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {f.name} ({Math.round(f.size / 1024)} KB)
                    </Typography>
                    <Button size="small" color="inherit" onClick={() => removeImageAt(idx)} sx={{ textTransform: 'none' }}>
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            fullWidth
            startIcon={<Send sx={{ color: 'white' }} />}
            onClick={handleSubmit}
            disabled={formData.rating === 0 || (!formData.comment.trim() && images.length === 0)}
            size="small"
            sx={{
              backgroundColor: '#a31830',
              color: '#ffffff',
              border: 'none',
              '&:hover': {
                    backgroundColor: '#8b1426',
                    boxShadow: '0 4px 12px rgba(163, 24, 48, 0.25)',
              },
              '&:disabled': { backgroundColor: 'action.disabled', color: 'text.disabled' },
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '0.8rem',
              py: 1
            }}
          >
            Submit Feedback
          </Button>
        </Box>

        {/* Right Panel - Feedback History & Guidelines */}
        <Box sx={{ 
          flex: 1, 
          width: isMobile ? '100%' : 'auto',
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Feedback History */}
          <Box sx={{ 
            flex: 1, 
            p: isMobile ? 2 : 3, 
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 400, mb: 2, color: 'text.primary', fontSize: '0.95rem' }}>
              Your Feedback History
            </Typography>

            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: 'text.secondary' }}>Loading...</Typography>
              </Box>
            ) : !Array.isArray(feedback) ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Unexpected response format
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {safeText(feedback)}
                </Typography>
              </Box>
            ) : feedback.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FeedbackIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
                <Typography sx={{ color: 'text.secondary' }}>
                  No feedback submitted yet
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  Submit your first feedback to get started
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                // Custom scrollbar styling - positioned at right edge
                '&::-webkit-scrollbar': {
                  width: '12px',
                },
                '&::-webkit-scrollbar-track': {
                  background: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f1f3f4',
                  borderRadius: '6px',
                  margin: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: (theme) => theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
                  borderRadius: '6px',
                  border: '2px solid transparent',
                  backgroundClip: 'content-box',
                  '&:hover': {
                    background: (theme) => theme.palette.mode === 'dark' ? '#777' : '#a8a8a8',
                    backgroundClip: 'content-box',
                  },
                },
                // Firefox scrollbar styling
                scrollbarWidth: 'thin',
                scrollbarColor: (theme) => theme.palette.mode === 'dark' ? '#555 #2a2a2a' : '#c1c1c1 #f1f3f4',
              }}>
                {feedback.map((raw: any) => {
                  const item = normalizeFeedbackItem(raw);
                  return (
                    <Paper key={item.id} sx={{ p: 2, mb: 1.5, backgroundColor: 'background.default', border: (theme) => `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            icon={getCategoryIcon(item.category)}
                            label={item.category}
                            size="small"
                            sx={{ 
                              backgroundColor: getCategoryColor(item.category), 
                              color: 'white', 
                              fontSize: '0.65rem', 
                              height: '20px',
                              px: 0.5,
                              '& .MuiChip-icon': { color: 'white', fontSize: '0.8rem' }
                            }}
                          />
                          <Chip
                            label={item.status}
                            size="small"
                            sx={{ 
                              backgroundColor: getStatusColor(item.status), 
                              color: 'white', 
                              fontSize: '0.65rem', 
                              height: '20px',
                              px: 0.5
                            }}
                          />
                        </Box>
                        <Rating
                          value={Number(item.rating) || 0}
                          readOnly
                          size="small"
                          sx={{ 
                            '& .MuiRating-iconFilled': { color: '#ffc107', fontSize: '1rem' },
                            '& .MuiRating-iconEmpty': { color: 'action.disabled', fontSize: '1rem' }
                          }}
                        />
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.primary', fontSize: '0.75rem' }}>
                        {item.comment || '— (No comment)'}
                      </Typography>

                      {Array.isArray(item.attachments) && item.attachments.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          {item.attachments.map((a: any) => {
                            const url = `${backendUrl}${a.url}`;
                            return (
                              <Box key={a.filename} sx={{ display: 'inline-flex' }}>
                                <FeedbackAttachmentThumb
                                  apiUrl={url}
                                  filename={a.filename}
                                  token={token}
                                  size={56}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                          Submitted on {safeFormatDate(item.created_at, 'MMM dd, yyyy HH:mm')}
                        </Typography>
                        {item.github_issue_url && (
                          <Link
                            href={item.github_issue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.3,
                              fontSize: '0.65rem',
                              color: 'text.secondary',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            <GitHubIcon sx={{ fontSize: '0.85rem' }} />
                            Issue #{item.github_issue_number}
                          </Link>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Feedback Guidelines */}
          <Box sx={{ p: 3, textAlign: isMobile ? 'center' : 'left' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary', fontSize: '0.95rem' }}>
              Feedback Guidelines
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center': 'left', mb: 1 }}>
                  <Chat sx={{ mr: 0.5, color: getCategoryColor('general'), fontSize: '0.9rem' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: getCategoryColor('general'), fontSize: '0.75rem' }}>
                    General Feedback
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                  Share thoughts about UX and suggestions for improvement.
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center': 'left', mb: 1 }}>
                  <BugReport sx={{ mr: 0.5, color: getCategoryColor('bug'), fontSize: '0.9rem' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: getCategoryColor('bug'), fontSize: '0.75rem' }}>
                    Bug Reports
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                  Include steps to reproduce, expected vs actual behavior.
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center': 'left', mb: 1 }}>
                  <Lightbulb sx={{ mr: 0.5, color: getCategoryColor('feature'), fontSize: '0.9rem' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: getCategoryColor('feature'), fontSize: '0.75rem' }}>
                    Feature Requests
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                  Describe the feature, its usefulness, and how it should work.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default Feedback;
